<!-- ODD ONE OUT — four things, one impostor, bonus for knowing why.

     Two beats per question: spot it, then say why. The why round is multiple
     choice because free text cannot be marked offline without turning a fast
     game into a spelling test — and it is worth real points, so it is not
     decoration.

     Three lives rather than sudden death: this is the thinking mode, and one
     unlucky read of a sneaky set should not end the whole thing. -->
<template>
  <IonPage>
    <AppHeader title="Odd One Out" back="/tabs/play" />

    <IonContent :fullscreen="true">
      <template v-if="!started">
        <div class="pad" style="padding-top: var(--sp-6)">
          <p class="ex-display lg">Odd One Out</p>
          <p class="ex-sub">
            Four things. One does not belong. Spot it, then say why for double.
            Fifteen seconds a go, three lives, and it gets sneaky.
          </p>
        </div>
        <ModeRecord mode="odd-one-out" />

        <div class="pad" style="margin-top: var(--sp-5)">
          <IonButton expand="block" size="large" @click="begin">Start spotting</IonButton>
        </div>
      </template>

      <template v-else-if="current">
        <GameHud :score="score" :streak="streak" :lives="lives" />
        <div v-if="timed" class="clock-bar" :class="clockClass" aria-hidden="true"><i :style="{ width: clockPct + '%' }" /></div>

        <div class="q-stage" ref="stageRef" tabindex="-1">
          <span class="q-topic">
            <span aria-hidden="true">{{ topicEmoji(current.topic) }}</span>
            {{ topicLabel(current.topic) }}
          </span>

          <p class="q-prompt">{{ phase === 'why' ? 'And why?' : current.prompt }}</p>

          <div v-if="phase === 'pick'" class="answers two-up">
            <button
              v-for="(item, i) in current.items"
              :key="i"
              type="button"
              class="answer"
              :class="pickClass(i)"
              :disabled="revealed"
              @click="pick(i)"
            >
              {{ item }}
              <span v-if="revealed && pickClass(i) === 'right'" class="sr-only"> — correct answer</span>
              <span v-else-if="revealed && pickClass(i) === 'wrong'" class="sr-only"> — your answer, incorrect</span>
            </button>
          </div>

          <div v-else class="answers">
            <p class="ex-sub" style="margin: 0">
              <strong>{{ current.items[current.answer] }}</strong> is the odd one. What makes it
              different?
            </p>
            <button
              v-for="(w, i) in current.whyOptions"
              :key="i"
              type="button"
              class="answer"
              :class="whyClass(i)"
              :disabled="whyRevealed"
              @click="pickWhy(i)"
            >
              {{ w }}
              <span v-if="whyRevealed && whyClass(i) === 'right'" class="sr-only"> — correct answer</span>
              <span v-else-if="whyRevealed && whyClass(i) === 'wrong'" class="sr-only"> — your answer, incorrect</span>
            </button>
          </div>
        </div>
      </template>

      <div class="tail" />
    </IonContent>

    <VerdictFlash :text="verdict.text" :why="verdict.why" :good="verdict.good" />
    <AusBonus :when="ausNow" @done="ausNow = false" />
  </IonPage>
</template>

<script setup>
import { ref, computed, onUnmounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { IonPage, IonContent, IonButton } from '@ionic/vue';
import { AppHeader, announce } from '../core/ui';
import GameHud from '../components/GameHud.vue';
import ModeRecord from '../components/ModeRecord.vue';
import VerdictFlash from '../components/VerdictFlash.vue';
import AusBonus from '../components/AusBonus.vue';
import { topicEmoji, topicLabel } from '../game/topics.js';
import { drawQuestions, pointsFor, isAusAnswer } from '../game/engine.js';
import { drawOptions } from '../game/profile.js';
import { useCountdown } from '../game/clock.js';
import { paced, timed } from '../game/pace.js';
import { regainLife } from '../game/lives.js';
import { finishRun } from '../game/run.js';
import { useStopOnLeave, useResetOnEnter } from '../game/guard.js';
import { pun } from '../game/puns.js';
import { haptic } from '../core/util.js';

const router = useRouter();
const PER_QUESTION_MS = 15000;

const started = ref(false);
const queue = ref([]);
const idx = ref(0);
const phase = ref('pick');
const revealed = ref(false);
const chosen = ref(-1);
const whyRevealed = ref(false);
const whyChosen = ref(-1);
const ausNow = ref(false);
const stageRef = ref(null);

const score = ref(0);
const streak = ref(0);
const bestChain = ref(0);
const correct = ref(0);
const wrong = ref(0);
const ausCount = ref(0);
const lives = ref(3);
const verdict = ref({ text: '', why: '', good: true });

const clock = useCountdown(paced(PER_QUESTION_MS), () => timeUp());
const current = computed(() => queue.value[idx.value] ?? null);
const clockPct = computed(() => (clock.remaining.value / paced(PER_QUESTION_MS)) * 100);
const clockClass = computed(() => {
  const s = clock.remaining.value / 1000;
  return s <= 4 ? 'panic' : s <= 8 ? 'warn' : '';
});

let advanceTimer = null;
let ended = false;

function begin() {
  queue.value = drawQuestions('odd-one-out', { ...drawOptions.value, count: 40 });
  started.value = true;
  clock.reset(paced(PER_QUESTION_MS));
  if (timed.value) clock.start();
  // The Start button is gone the instant this runs, so without this a
  // keyboard or screen-reader user is dropped back to <body>.
  nextTick(() => stageRef.value?.focus());
}

function pickClass(i) {
  if (!revealed.value) return '';
  if (i === current.value.answer) return 'right';
  if (i === chosen.value) return 'wrong';
  return 'muted';
}

function whyClass(i) {
  if (!whyRevealed.value) return '';
  if (i === current.value.whyAnswer) return 'right';
  if (i === whyChosen.value) return 'wrong';
  return 'muted';
}

function pick(i) {
  if (revealed.value || ended) return;
  clock.stop();
  const q = current.value;
  revealed.value = true;
  chosen.value = i;
  const right = i === q.answer;

  if (right) {
    const aus = isAusAnswer(q, q.items[q.answer]);
    if (aus) { ausCount.value += 1; ausNow.value = true; }
    score.value += pointsFor({ difficulty: q.difficulty, streak: streak.value, aus });
    streak.value += 1;
    regainLife(lives);
    bestChain.value = Math.max(bestChain.value, streak.value);
    correct.value += 1;
    haptic('success');
    verdict.value = { text: streak.value >= 3 ? pun('streak') : pun('correct'), why: '', good: true };
    // Straight into the bonus round rather than a separate screen — the why is
    // part of the same question, and pushing a route for it would put a back
    // button on a thing you cannot go back from.
    clearTimeout(advanceTimer);
    advanceTimer = setTimeout(() => {
      phase.value = 'why';
      verdict.value = { text: '', why: '', good: true };
      // The pick buttons are swapped out for the why-round ones on the same
      // frame — without this a screen reader keeps reading stale content.
      announce('And why? What makes it different?');
      nextTick(() => stageRef.value?.focus());
    }, 700);
  } else {
    loseALife(q.reason);
  }
}

function pickWhy(i) {
  if (whyRevealed.value || ended) return;
  const q = current.value;
  whyRevealed.value = true;
  whyChosen.value = i;
  const right = i === q.whyAnswer;

  if (right) {
    score.value += pointsFor({ difficulty: q.difficulty, streak: streak.value });
    haptic('success');
    verdict.value = { text: 'Austin-tatious. Double points.', why: q.reason, good: true };
  } else {
    // The why is a BONUS. Missing it costs the bonus and nothing else — no
    // life, no streak. Punishing a wrong reason twice would stop him guessing.
    haptic('warning');
    verdict.value = { text: 'Right one, wrong reason.', why: q.reason, good: false };
  }
  clearTimeout(advanceTimer);
  advanceTimer = setTimeout(next, 2000);
}

function timeUp() {
  if (ended) return;
  if (phase.value === 'why') { next(); return; }
  if (revealed.value) return;
  revealed.value = true;
  chosen.value = -1;
  loseALife(current.value?.reason ?? '', pun('clock'));
}

function loseALife(why, text) {
  wrong.value += 1;
  streak.value = 0;
  lives.value -= 1;
  haptic('error');
  verdict.value = { text: text ?? pun('wrong'), why, good: false };
  clearTimeout(advanceTimer);
  advanceTimer = setTimeout(() => (lives.value <= 0 ? end() : next()), 2100);
}

function next() {
  if (ended) return;
  verdict.value = { text: '', why: '', good: true };
  phase.value = 'pick';
  revealed.value = false;
  whyRevealed.value = false;
  chosen.value = -1;
  whyChosen.value = -1;
  idx.value += 1;
  if (idx.value >= queue.value.length) {
    queue.value = [...queue.value, ...drawQuestions('odd-one-out', { ...drawOptions.value, count: 20 })];
  }
  clock.reset(paced(PER_QUESTION_MS));
  if (timed.value) clock.start();
  announce(`Next: ${current.value?.prompt ?? ''}`);
}

async function end() {
  if (ended) return;
  ended = true;
  clock.stop();
  clearTimeout(advanceTimer);
  await finishRun({
    mode: 'odd-one-out',
    score: score.value,
    lastedMs: 0,
    correct: correct.value,
    wrong: wrong.value,
    bestChain: bestChain.value,
    ausBonuses: ausCount.value,
  });
  router.replace('/tabs/play/results');
}

// Everything this screen has running, stopped. Idempotent: it is called on
// every leave, on abandon, and again before every re-entry.
function teardown() {
  ended = true;
  clock.stop();
  clearTimeout(advanceTimer);
}

// Back to the pre-game screen. Without this, "Go again" returned to this same
// cached instance still holding the finished run.
function resetRun() {
  teardown();
  ended = false;
  // Every ref back to the literal it was declared with — the state a
  // fresh mount would have. Resetting only the run flags left the LAST
  // question's verdict and its revealed answer painted over the new
  // board, so a new game opened showing why the previous one ended.
  started.value = false;
  queue.value = [];
  idx.value = 0;
  phase.value = 'pick';
  revealed.value = false;
  chosen.value = -1;
  whyRevealed.value = false;
  whyChosen.value = -1;
  ausNow.value = false;
  stageRef.value = null;
  score.value = 0;
  streak.value = 0;
  bestChain.value = 0;
  correct.value = 0;
  wrong.value = 0;
  ausCount.value = 0;
  lives.value = 3;
  verdict.value = { text: '', why: '', good: true };
}

useStopOnLeave(teardown);
useResetOnEnter(resetRun);

onUnmounted(() => { ended = true; clearTimeout(advanceTimer); });
</script>
